import { Currency, formatCLP, formatUF } from '../../../common/format';
import { BaseMonthlyPolicyRow, BasePolicyRow } from '../types';

export interface ProjectionColumn<TRow = BasePolicyRow> {
  id: keyof TRow & string;
  label: string;
  isMoney: boolean;
}

// Canonical column order shared by every playground. Playground-specific columns
// (Aporte fiscal, Retiro, VP pre/post-retiro) are injected BETWEEN the head and the
// tail via the builders below, so they always land right after "Valor póliza" and
// before "Monto rescatable" / "Indemn. fallecimiento".
const YEARLY_HEAD_COLUMNS: ProjectionColumn[] = [
  { id: 'year', label: 'Año', isMoney: false },
  { id: 'age', label: 'Edad', isMoney: false },
  { id: 'actuarial_age', label: 'Edad actuarial', isMoney: false },
  { id: 'accumulated_prime', label: 'Prima acumulada', isMoney: true },
  { id: 'coverage_costs', label: 'Costo coberturas', isMoney: true },
  { id: 'policy_expenses', label: 'Gastos póliza', isMoney: true },
  { id: 'policy_value', label: 'Valor póliza', isMoney: true },
];

const PROJECTION_TAIL_COLUMNS: ProjectionColumn[] = [
  { id: 'withdrawable_amount', label: 'Monto rescatable', isMoney: true },
  { id: 'death_compensation', label: 'Indemn. fallecimiento', isMoney: true },
];

const MONTH_COLUMN: ProjectionColumn<BaseMonthlyPolicyRow> = {
  id: 'month',
  label: 'Mes',
  isMoney: false,
};

// The head/tail columns only reference keys shared by every row type, so the cast
// through `unknown` to the caller's TRow is safe — TS can't infer the overlap across
// the two distinct base-row types on its own.
export function buildProjectionColumns<TRow extends BasePolicyRow = BasePolicyRow>(
  middle: ProjectionColumn<TRow>[] = [],
): ProjectionColumn<TRow>[] {
  return [
    ...(YEARLY_HEAD_COLUMNS as unknown as ProjectionColumn<TRow>[]),
    ...middle,
    ...(PROJECTION_TAIL_COLUMNS as unknown as ProjectionColumn<TRow>[]),
  ];
}

export function buildMonthlyProjectionColumns<
TRow extends BaseMonthlyPolicyRow = BaseMonthlyPolicyRow>(
  middle: ProjectionColumn<TRow>[] = [],
): ProjectionColumn<TRow>[] {
  return [
    MONTH_COLUMN as unknown as ProjectionColumn<TRow>,
    ...(YEARLY_HEAD_COLUMNS as unknown as ProjectionColumn<TRow>[]),
    ...middle,
    ...(PROJECTION_TAIL_COLUMNS as unknown as ProjectionColumn<TRow>[]),
  ];
}

export const COMMON_MONTHLY_PROJECTION_COLUMNS:
ProjectionColumn<BaseMonthlyPolicyRow>[] = buildMonthlyProjectionColumns();

export const COMMON_PROJECTION_COLUMNS: ProjectionColumn[] = buildProjectionColumns();

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function formatMonthLabel(monthNumber: number): string {
  const today = new Date();
  const calendarMonthIndex = (today.getMonth() + monthNumber) % 12;
  return `${monthNumber} — ${MONTH_NAMES[calendarMonthIndex]}`;
}

export function renderProjectionCell<TRow extends Record<string, number | undefined>>(
  row: TRow,
  col: ProjectionColumn<TRow>,
  currency: Currency,
  ufValue: number,
): string {
  const raw = row[col.id];
  if (raw === undefined || raw === null) return '—';
  if (col.id === 'month') return formatMonthLabel(Number(raw));
  if (!col.isMoney) return String(raw);
  const num = Number(raw);
  const value = currency === 'CLP' ? num * ufValue : num;
  return currency === 'CLP' ? formatCLP(value) : formatUF(value);
}
